import { PencilSimple } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Badge, type BadgeProps } from "@/components/primitives/Badge";
import { Button, IconButton, type ButtonProps } from "@/components/primitives/Button";

export type EntityDisplayCardMetadata = {
  label: ReactNode;
  value: ReactNode;
};

export type EntityDisplayCardAction = {
  id: string;
  label: ReactNode;
  ariaLabel?: string;
  icon?: ReactNode;
  disabled?: boolean;
  variant?: ButtonProps["variant"];
};

export type EntityDisplayCardProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  statusTone?: BadgeProps["tone"];
  metadata?: EntityDisplayCardMetadata[];
  actions?: EntityDisplayCardAction[];
  onAction?: (action: EntityDisplayCardAction) => void;
};

export const EntityDisplayCard = forwardRef<HTMLDivElement, EntityDisplayCardProps>(({ className, title, description, status, statusTone = "success", metadata = [], actions = [], onAction, ...props }, ref) => (
  <div ref={ref} className={cn("bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-px-4 bfrs-py-3", className)} {...props}>
    <div className="bfrs-flex bfrs-items-start bfrs-gap-3">
      <div className="bfrs-min-w-0 bfrs-flex-1">
        <div className="bfrs-flex bfrs-min-w-0 bfrs-flex-wrap bfrs-items-center bfrs-gap-2">
          <span className="bfrs-truncate bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">{title}</span>
          {status && <Badge tone={statusTone}>{status}</Badge>}
        </div>
        {description && <div className="bfrs-mt-1 bfrs-text-xs bfrs-leading-5 bfrs-text-text-secondary">{description}</div>}
      </div>
      {actions.length > 0 && (
        <div className="bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-gap-1">
          {actions.map((action) =>
            action.icon ? (
              <IconButton key={action.id} icon={action.icon} aria-label={action.ariaLabel ?? String(action.label)} variant={action.variant ?? "ghost"} size="sm" disabled={action.disabled} onClick={() => onAction?.(action)} />
            ) : (
              <Button key={action.id} variant={action.variant ?? "ghost"} size="sm" disabled={action.disabled} leftIcon={<PencilSimple size={13} />} onClick={() => onAction?.(action)}>
                {action.label}
              </Button>
            )
          )}
        </div>
      )}
    </div>
    {metadata.length > 0 && (
      <dl className="bfrs-mt-3 bfrs-grid bfrs-gap-2 md:bfrs-grid-cols-2">
        {metadata.map((item, index) => (
          <div key={index} className="bfrs-min-w-0">
            <dt className="bfrs-text-[11px] bfrs-font-semibold bfrs-uppercase bfrs-text-text-muted">{item.label}</dt>
            <dd className="bfrs-mt-0.5 bfrs-truncate bfrs-text-xs bfrs-font-medium bfrs-text-text-primary">{item.value}</dd>
          </div>
        ))}
      </dl>
    )}
  </div>
));

EntityDisplayCard.displayName = "EntityDisplayCard";
